'use client';

import { useMemberAnalytics } from '@/analytics/members.analytics';
import FilterCount from '@/components/ui/filter-count';
import ViewType from '@/components/ui/view-type';
import useUpdateQueryParams from '@/hooks/useUpdateQueryParams';
import { IMembersSearchParams } from '@/types/members.types';
import { IUserInfo } from '@/types/shared.types';
import { getAnalyticsUserInfo, getFilterCount, getQuery, triggerLoader } from '@/utils/common.utils';
import { EVENTS, SORT_OPTIONS, VIEW_TYPE_OPTIONS } from '@/utils/constants';
import { SortDropdown } from '@/components/common/filters/SortDropdown';
import Image from 'next/image';
import { ChangeEvent, useEffect, useRef, useState } from 'react';

const MEMBERS_SORT_OPTIONS = [
  { value: SORT_OPTIONS.ASCENDING, label: 'A-Z (Ascending)' },
  { value: SORT_OPTIONS.DESCENDING, label: 'Z-A (Descending)' },
];

interface IMembersToolbar {
  searchParams: Record<string, string>;
  totalMembers: number;
  userInfo: IUserInfo | undefined;
}

const MembersToolbar = (props: IMembersToolbar) => {
  const totalMembers = props?.totalMembers;
  const { updateQueryParams } = useUpdateQueryParams();
  const userInfo = props?.userInfo;
  const searchParams = props?.searchParams;

  const searchBy = searchParams['searchBy'] || '';
  const sortBy = searchParams['sort'] || SORT_OPTIONS.ASCENDING;
  const view = searchParams['viewType'] || VIEW_TYPE_OPTIONS.GRID;

  const inputRef = useRef<HTMLInputElement>(null);
  const [searchInput, setSearchInput] = useState(searchBy);
  const analytics = useMemberAnalytics();

  const query = getQuery(searchParams);
  const filterCount = getFilterCount(query);

  const onFilterClickHandler = () => {
    document.dispatchEvent(new CustomEvent(EVENTS.SHOW_MEMBERS_FILTER, { detail: true }));
  };

  const onViewtypeClickHandler = (type: string) => {
    analytics.onViewTypeClicked(getAnalyticsUserInfo(userInfo), type);
    if (view === type) {
      return;
    }
    triggerLoader(true);
    if (type !== VIEW_TYPE_OPTIONS.GRID) {
      updateQueryParams('viewType', type, searchParams);
      return;
    }
    updateQueryParams('viewType', type, searchParams);
  };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e?.target?.value);
  };

  const onClearSearchClicked = () => {
    setSearchInput('');
    if (searchParams['searchBy']) {
      triggerLoader(true);
    }
    updateQueryParams('searchBy', '', searchParams);
  };

  const onSubmitHandler = (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (searchBy.trim() !== searchInput.trim()) {
      triggerLoader(true);
    }
    analytics.onSearchClicked(searchInput, getAnalyticsUserInfo(userInfo));
    updateQueryParams('searchBy', searchInput, searchParams);
  };

  const onSortChange = (value: string) => {
    if (value === sortBy) return;
    triggerLoader(true);
    analytics.onSorByClicked(getAnalyticsUserInfo(userInfo), value);
    updateQueryParams('sort', value, searchParams);
  };

  useEffect(() => {
    setSearchInput(searchBy);
  }, [searchParams]);

  return (
    <>
      <div className="toolbar">
        <div className="toolbar__left">
          <button className="toolbar__left__filterbtn" onClick={onFilterClickHandler}>
            <img loading="lazy" alt="filter" src="/icons/filter.svg" height={20} width={20}></img>
            {filterCount > 0 && <FilterCount count={filterCount} />}
          </button>
          <div className="toolbar__left__title-container">
            <h1 className="toolbar__left__title-container__title">Members</h1>
            <p className="toolbar__left__title__container__count">({totalMembers})</p>
          </div>
          <div className="toolbar__left__search-container">
            <form className="toolbar__left__search-container__searchfrm" onSubmit={onSubmitHandler}>
              <input
                ref={inputRef}
                value={searchInput}
                onChange={(e) => onInputChange(e)}
                className="toolbar__left__search-container__searchfrm__input"
                placeholder="Search by Member Name, Team, or Project"
                onFocus={(e) =>
                  e.currentTarget.setSelectionRange(e.currentTarget.value.length, e.currentTarget.value.length)
                }
              />
              <div className="toolbar__left__search-container__searchfrm__optns">
                {searchInput && (
                  <button
                    type="button"
                    onClick={onClearSearchClicked}
                    className="toolbar__left__search-container__searchfrm__optns__clrbtn"
                  >
                    <Image loading="lazy" alt="close" src="/icons/close-gray.svg" height={16} width={16} />
                  </button>
                )}
                <button className="toolbar__left__search-container__searchfrm__searchbtn" type="submit">
                  <img loading="lazy" alt="search" src="/icons/search.svg" height={16} width={16} />
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="toolbar__right">
          <SortDropdown
            sortByLabel="Sort by:"
            options={MEMBERS_SORT_OPTIONS}
            currentSort={sortBy}
            onSortChange={onSortChange}
          />
        </div>
      </div>

      <style jsx>
        {`
          .toolbar {
            display: flex;
            justify-content: space-between;
            height: 40px;
            gap: 8px;
            width: 100%;
          }

          .toolbar__left {
            display: flex;
            gap: 8px;
          }

          .toolbar__left__filterbtn {
            padding: 8px 12px 8px 12px;
            border: none;
            display: flex;
            gap: 8px;
            align-items: center;
            border-radius: 8px;
            background: #fff;
            height: inherit;
            box-shadow: 0px 1px 2px 0px rgba(15, 23, 42, 0.16);
          }

          .toolbar__left__search-container__searchfrm__optns__clrbtn {
            background: inherit;
            outline: none;
            position: absolute;
            bottom: 0;
            height: 16px;
            left: 0;
            top: 0;
          }

          .toolbar__left__title-container {
            position: absolute;
            width: 1px;
            height: 1px;
            padding: 0;
            margin: -1px;
            overflow: hidden;
            clip: rect(0 0 0 0);
            white-space: nowrap;
            border: 0;
          }

          .toolbar__left__search-container {
            height: inherit;
            border-radius: 4px;
            background: #fff;
            box-shadow: 0px 1px 2px 0px #0f172a29;
            background: #fff;
            display: none;
          }

          .toolbar__left__search-container__searchfrm {
            display: flex;
            height: 40px;
            padding: 8px 12px;
            border-radius: 4px;
            align-items: center;
          }

          ::-webkit-input-placeholder {
            color: #94a3b8;
            font-size: 14px;
            font-weight: 500;
            line-height: 24px;
          }

          :-moz-placeholder {
            color: #94a3b8;
            font-size: 14px;
            font-weight: 500;
            line-height: 24px;
          }

          ::-moz-placeholder {
            color: #94a3b8;
            font-size: 14px;
            font-weight: 500;
            line-height: 24px;
          }

          :-ms-input-placeholder {
            color: #94a3b8;
            font-size: 14px;
            font-weight: 500;
            line-height: 24px;
          }

          ::input-placeholder {
            color: #94a3b8;
            font-size: 14px;
            font-weight: 500;
            line-height: 24px;
          }

          ::placeholder {
            color: #94a3b8;
            font-size: 14px;
            font-weight: 500;
            line-height: 24px;
          }

          .toolbar__left__search-container__searchfrm__optns {
            display: flex;
            width: 40px;
            justify-content: end;
            position: relative;
            align-items: center;
            gap: 10px;
          }

          .toolbar__left__search-container__searchfrm__input {
            border: none;
            width: 111px;
            background-color: #fff;
            padding-right: 4px;
            color: black;
            &:focus {
              outline: none;
            }
          }

          .toolbar__left__search-container__searchfrm__searchbtn {
            border: none;
            background: #fff;
            height: inherit;
            display: flex;
            align-items: center;
          }

          .toolbar__right {
            display: flex;
            gap: 8px;
            align-items: center;
          }

          @media (min-width: 1024px) {
            .toolbar__right {
              gap: 16px;
            }

            .toolbar {
              background-color: #f1f5f9;
            }
            .toolbar__left {
              gap: 16px;
            }

            .toolbar__left__filterbtn {
              display: none;
            }

            .toolbar__left__title-container {
              position: static;
              width: auto;
              height: auto;
              margin: 0;
              overflow: visible;
              clip: auto;
              white-space: normal;
              display: flex;
              gap: 8px;
              align-items: baseline;
            }

            .toolbar__left__search-container__searchfrm__input {
              width: 280px;
            }

            .toolbar__left__title-container__title {
              color: #0f172a;
              font-size: 28px;
              font-weight: 700;
              line-height: 40px;
            }

            .toolbar__left__title__container__count {
              color: #64748b;
              font-size: 14px;
              font-weight: 400;
              line-height: 20px;
            }
          }
        `}
      </style>
    </>
  );
};

export default MembersToolbar;
